// File: /var/www/app2/frontend/src/utils/generateDashboardPDF.ts

import jsPDF from "jspdf";

export interface DashboardStats {
  totalReports: number;
  pendingReview: number;
  approved: number;
  rejected: number;
  currentMonthSubmissions: number;
  totalMissionaries: number;
  activeMissionaries: number;
  submittedThisMonth: number;
  pendingSubmissions: number;
  submissionRate: number;
}

export interface MinistryPeriodStats {
  heardGospel?: number;
  receivedJesus?: number;
  baptized?: number;
  bibleStudies?: number;
  discoveryBibleStudyStarted?: number;
  churchesPlanted?: number;
  leadersTrained?: number;
}

export interface MinistryStats {
  currentMonth?: MinistryPeriodStats;
  priorMonth?: MinistryPeriodStats;
  ytd?: MinistryPeriodStats;
  changes?: MinistryPeriodStats;
}

export interface PDFFilters {
  year: string | number;
  month: string;
  country?: string;
  state: string;
  missionaryId: string;
  missionaryName?: string;
}

export interface CountryBreakdown {
  country: string;
  count: number;
}

export interface DashboardPDFData {
  stats: DashboardStats;
  ministryStats: MinistryStats;
  currentPeriod: {
    monthName?: string;
    year?: number;
  };
  filters: PDFFilters;
  generatedBy?: string;
  isAdmin?: boolean;
  countryBreakdown?: CountryBreakdown[];
}

const getMonthName = (monthNumber: number | string): string => {
  const months = [
    "",
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];
  const num = typeof monthNumber === "string" ? parseInt(monthNumber, 10) : monthNumber;
  return months[num] || "";
};

const formatNumber = (num?: number): string => {
  return new Intl.NumberFormat().format(num || 0);
};

export const generateDashboardPDF = async (data: DashboardPDFData): Promise<void> => {
  const pdf = new jsPDF("p", "mm", "a4");
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const compact = data.isAdmin === false;
  let currentY = compact ? 12 : 20;

  // Helper function to check if we need a new page
  const checkNewPage = (neededHeight: number): boolean => {
    if (currentY + neededHeight > pageHeight - (compact ? 15 : 25)) {
      pdf.addPage();
      currentY = compact ? 12 : 20;
      return true;
    }
    return false;
  };

  // Helper to draw a section header
  const drawSectionHeader = (title: string): void => {
    checkNewPage(20);
    const headerH = compact ? 8 : 10;
    pdf.setFillColor(30, 60, 114); // Dark blue
    pdf.rect(10, currentY, pageWidth - 20, headerH, "F");
    pdf.setFontSize(compact ? 10 : 12);
    pdf.setFont("helvetica", "bold");
    pdf.setTextColor(255, 255, 255);
    pdf.text(title, 15, currentY + (compact ? 6 : 7));
    pdf.setTextColor(0, 0, 0);
    currentY += headerH + (compact ? 3 : 5);
  };

  // Helper to draw a stat box
  const drawStatBox = (
    x: number,
    y: number,
    width: number,
    height: number,
    label: string,
    value: string | number,
    subtitle?: string,
    color?: [number, number, number]
  ): void => {
    // Box background
    pdf.setFillColor(248, 249, 250);
    pdf.setDrawColor(220, 220, 220);
    pdf.roundedRect(x, y, width, height, 2, 2, "FD");

    // Label
    pdf.setFontSize(8);
    pdf.setFont("helvetica", "normal");
    pdf.setTextColor(113, 128, 150);
    pdf.text(label.toUpperCase(), x + 5, y + 8);

    // Value
    pdf.setFontSize(18);
    pdf.setFont("helvetica", "bold");
    if (color) {
      pdf.setTextColor(color[0], color[1], color[2]);
    } else {
      pdf.setTextColor(45, 55, 72);
    }
    pdf.text(String(value), x + 5, y + 20);

    // Subtitle
    if (subtitle) {
      pdf.setFontSize(8);
      pdf.setFont("helvetica", "normal");
      pdf.setTextColor(102, 102, 102);
      pdf.text(subtitle, x + 5, y + 27);
    }

    pdf.setTextColor(0, 0, 0);
  };

  // Helper to draw ministry stat card
  const ministryCardHeight = compact ? 26 : 35;
  const drawMinistryCard = (
    x: number,
    y: number,
    width: number,
    label: string,
    current: number,
    prior: number,
    ytd: number,
    change?: number,
    color?: [number, number, number]
  ): number => {
    // Box background
    pdf.setFillColor(255, 255, 255);
    pdf.setDrawColor(220, 220, 220);
    pdf.roundedRect(x, y, width, ministryCardHeight, 2, 2, "FD");

    // Label
    pdf.setFontSize(compact ? 7 : 8);
    pdf.setFont("helvetica", "bold");
    pdf.setTextColor(113, 128, 150);
    pdf.text(label.toUpperCase(), x + 4, y + (compact ? 6 : 8));

    // Current value
    pdf.setFontSize(compact ? 12 : 16);
    pdf.setFont("helvetica", "bold");
    if (color) {
      pdf.setTextColor(color[0], color[1], color[2]);
    } else {
      pdf.setTextColor(45, 55, 72);
    }
    pdf.text(formatNumber(current), x + 4, y + (compact ? 14 : 18));

    // Change indicator
    if (change !== undefined) {
      pdf.setFontSize(compact ? 7 : 8);
      if (change > 0) {
        pdf.setTextColor(40, 167, 69);
        pdf.text(`+${change}%`, x + 35, y + (compact ? 14 : 18));
      } else if (change < 0) {
        pdf.setTextColor(220, 53, 69);
        pdf.text(`${change}%`, x + 35, y + (compact ? 14 : 18));
      } else {
        pdf.setTextColor(102, 102, 102);
        pdf.text("0%", x + 35, y + (compact ? 14 : 18));
      }
    }

    // Prior and YTD
    pdf.setFontSize(compact ? 6 : 7);
    pdf.setFont("helvetica", "normal");
    pdf.setTextColor(102, 102, 102);
    pdf.text(`Prior: ${formatNumber(prior)}`, x + 4, y + (compact ? 20 : 26));
    pdf.text(`YTD: ${formatNumber(ytd)}`, x + 4, y + (compact ? 24 : 32));

    pdf.setTextColor(0, 0, 0);
    return ministryCardHeight;
  };

  // =====================
  // PDF CONTENT STARTS
  // =====================

  // Title
  pdf.setFontSize(compact ? 18 : 22);
  pdf.setFont("helvetica", "bold");
  pdf.setTextColor(30, 60, 114);
  const pdfTitle = data.isAdmin ? "USA Office Dashboard Report" : "Supporter Dashboard Report";
  pdf.text(pdfTitle, pageWidth / 2, currentY, { align: "center" });
  currentY += compact ? 8 : 12;

  // Applied Filters Section
  const hasFilters =
    data.filters.month ||
    data.filters.state ||
    data.filters.missionaryId ||
    data.filters.country;

  pdf.setFillColor(240, 248, 255);
  pdf.setDrawColor(200, 220, 240);
  const filterBoxHeight = compact ? (hasFilters ? 20 : 14) : (hasFilters ? 28 : 18);
  pdf.roundedRect(10, currentY, pageWidth - 20, filterBoxHeight, 2, 2, "FD");

  pdf.setFontSize(compact ? 10 : 12);
  pdf.setFont("helvetica", "bold");
  pdf.setTextColor(30, 60, 114);
  pdf.text("Report Filters:", 15, currentY + (compact ? 7 : 9));

  pdf.setFontSize(compact ? 10 : 12);
  pdf.setFont("helvetica", "bold");
  pdf.setTextColor(45, 55, 72);

  const filterParts: string[] = [`Year: ${data.filters.year}`];
  if (data.filters.month) {
    filterParts.push(`Month: ${getMonthName(data.filters.month)}`);
  }
  if (data.filters.country) {
    filterParts.push(`Country: ${data.filters.country}`);
  }
  if (data.filters.state) {
    filterParts.push(`State: ${data.filters.state}`);
  }
  if (data.filters.missionaryName) {
    filterParts.push(`Missionary: ${data.filters.missionaryName}`);
  }

  pdf.text(filterParts.join("  |  "), 58, currentY + (compact ? 7 : 9));

  if (hasFilters) {
    pdf.setFontSize(compact ? 7 : 8);
    pdf.setFont("helvetica", "normal");
    pdf.setTextColor(100, 100, 100);
    pdf.text(
      "Note: Statistics below are filtered based on the criteria above.",
      15,
      currentY + (compact ? 14 : 20)
    );
  }

  currentY += filterBoxHeight + (compact ? 4 : 10);

  const { stats } = data;

  // =====================
  // REPORT REVIEW STATUS (admin only)
  // =====================
  if (data.isAdmin !== false) {
    drawSectionHeader("Report Review Status");

    const cardWidth = (pageWidth - 30) / 4 - 3;
    const cardHeight = 32;

    // Row of 4 cards
    drawStatBox(
      10,
      currentY,
      cardWidth,
      cardHeight,
      "Total Reports",
      formatNumber(stats.totalReports),
      "All time"
    );

    drawStatBox(
      10 + cardWidth + 4,
      currentY,
      cardWidth,
      cardHeight,
      "Pending Review",
      formatNumber(stats.pendingReview),
      "Needs attention",
      [255, 165, 0]
    );

    const approvalRate =
      stats.totalReports > 0
        ? Math.round((stats.approved / stats.totalReports) * 100)
        : 0;
    drawStatBox(
      10 + (cardWidth + 4) * 2,
      currentY,
      cardWidth,
      cardHeight,
      "Approved",
      formatNumber(stats.approved),
      `${approvalRate}% approval rate`,
      [40, 167, 69]
    );

    drawStatBox(
      10 + (cardWidth + 4) * 3,
      currentY,
      cardWidth,
      cardHeight,
      "This Month",
      formatNumber(stats.currentMonthSubmissions),
      "Reports submitted",
      [102, 126, 234]
    );

    currentY += cardHeight + 15;
  }

  // =====================
  // YEAR TO DATE MINISTRY IMPACT
  // =====================
  drawSectionHeader("Ministry Impact Statistics");

  const { ministryStats } = data;
  const ministryCardWidth = (pageWidth - 30) / 3 - 3;

  // Row 1: People Reached, Conversions, Baptisms
  drawMinistryCard(
    10,
    currentY,
    ministryCardWidth,
    "People Reached",
    ministryStats.currentMonth?.heardGospel || 0,
    ministryStats.priorMonth?.heardGospel || 0,
    ministryStats.ytd?.heardGospel || 0,
    ministryStats.changes?.heardGospel,
    [30, 60, 114]
  );

  drawMinistryCard(
    10 + ministryCardWidth + 4,
    currentY,
    ministryCardWidth,
    "Conversions",
    ministryStats.currentMonth?.receivedJesus || 0,
    ministryStats.priorMonth?.receivedJesus || 0,
    ministryStats.ytd?.receivedJesus || 0,
    ministryStats.changes?.receivedJesus,
    [40, 167, 69]
  );

  drawMinistryCard(
    10 + (ministryCardWidth + 4) * 2,
    currentY,
    ministryCardWidth,
    "Baptisms",
    ministryStats.currentMonth?.baptized || 0,
    ministryStats.priorMonth?.baptized || 0,
    ministryStats.ytd?.baptized || 0,
    ministryStats.changes?.baptized,
    [66, 153, 225]
  );

  currentY += ministryCardHeight + (compact ? 3 : 5);

  // Row 2: Bible Studies, Churches, Leaders
  const bibleStudies =
    ministryStats.currentMonth?.bibleStudies ||
    ministryStats.currentMonth?.discoveryBibleStudyStarted ||
    0;
  const priorBibleStudies =
    ministryStats.priorMonth?.bibleStudies ||
    ministryStats.priorMonth?.discoveryBibleStudyStarted ||
    0;
  const ytdBibleStudies =
    ministryStats.ytd?.bibleStudies ||
    ministryStats.ytd?.discoveryBibleStudyStarted ||
    0;
  const changeBibleStudies =
    ministryStats.changes?.bibleStudies ||
    ministryStats.changes?.discoveryBibleStudyStarted;

  drawMinistryCard(
    10,
    currentY,
    ministryCardWidth,
    "Bible Studies",
    bibleStudies,
    priorBibleStudies,
    ytdBibleStudies,
    changeBibleStudies,
    [102, 126, 234]
  );

  drawMinistryCard(
    10 + ministryCardWidth + 4,
    currentY,
    ministryCardWidth,
    "Churches Planted",
    ministryStats.currentMonth?.churchesPlanted || 0,
    ministryStats.priorMonth?.churchesPlanted || 0,
    ministryStats.ytd?.churchesPlanted || 0,
    ministryStats.changes?.churchesPlanted,
    [245, 101, 101]
  );

  drawMinistryCard(
    10 + (ministryCardWidth + 4) * 2,
    currentY,
    ministryCardWidth,
    "Leaders Trained",
    ministryStats.currentMonth?.leadersTrained || 0,
    ministryStats.priorMonth?.leadersTrained || 0,
    ministryStats.ytd?.leadersTrained || 0,
    ministryStats.changes?.leadersTrained,
    [237, 137, 54]
  );

  currentY += ministryCardHeight + (compact ? 3 : 10);

  // =====================
  // MISSIONARY OVERVIEW
  // =====================
  drawSectionHeader(data.isAdmin !== false ? "Missionary Overview" : "My Missionaries");

  // Colored gradient-style cards (simulated with solid colors)
  const cardHeight = data.isAdmin !== false ? 40 : 28;
  const drawMissionaryCard = (
    x: number,
    y: number,
    width: number,
    label: string,
    value: number | string,
    bgColor: [number, number, number]
  ): void => {
    pdf.setFillColor(bgColor[0], bgColor[1], bgColor[2]);
    pdf.roundedRect(x, y, width, cardHeight, 3, 3, "F");

    if (data.isAdmin !== false) {
      pdf.setFontSize(9);
      pdf.setFont("helvetica", "bold");
      pdf.setTextColor(255, 255, 255);
      pdf.text(label.toUpperCase(), x + width / 2, y + 12, { align: "center" });
      pdf.setFontSize(20);
      pdf.text(String(value), x + width / 2, y + 30, { align: "center" });
    } else {
      pdf.setFontSize(8);
      pdf.setFont("helvetica", "bold");
      pdf.setTextColor(255, 255, 255);
      pdf.text(label.toUpperCase(), x + width / 2, y + 9, { align: "center" });
      pdf.setFontSize(16);
      pdf.text(String(value), x + width / 2, y + 22, { align: "center" });
    }

    pdf.setTextColor(0, 0, 0);
  };

  if (data.isAdmin !== false) {
    const missionaryCardWidth = (pageWidth - 30) / 4 - 3;

    drawMissionaryCard(
      10,
      currentY,
      missionaryCardWidth,
      "Total Missionaries",
      stats.totalMissionaries,
      [102, 126, 234]
    );

    drawMissionaryCard(
      10 + missionaryCardWidth + 4,
      currentY,
      missionaryCardWidth,
      "Submitted This Month",
      stats.submittedThisMonth,
      [72, 187, 120]
    );

    drawMissionaryCard(
      10 + (missionaryCardWidth + 4) * 2,
      currentY,
      missionaryCardWidth,
      "Pending Submissions",
      stats.pendingSubmissions,
      [255, 165, 0]
    );

    drawMissionaryCard(
      10 + (missionaryCardWidth + 4) * 3,
      currentY,
      missionaryCardWidth,
      "Submission Rate",
      stats.submissionRate,
      [66, 153, 225]
    );

    // Add % symbol for submission rate
    pdf.setFontSize(12);
    pdf.setTextColor(255, 255, 255);
    pdf.text(
      "%",
      10 + (missionaryCardWidth + 4) * 3 + missionaryCardWidth / 2 + 15,
      currentY + 30
    );
    pdf.setTextColor(0, 0, 0);
  } else {
    // Supporter: show active count + country breakdown
    const breakdown = data.countryBreakdown || [];
    const totalCards = 1 + breakdown.length;
    const missionaryCardWidth = (pageWidth - 30) / Math.max(totalCards, 2) - 3;
    const colors: [number, number, number][] = [[102, 126, 234], [72, 187, 120], [66, 153, 225], [237, 137, 54]];

    drawMissionaryCard(10, currentY, missionaryCardWidth, "Active Missionaries", stats.totalMissionaries, colors[0]);

    breakdown.forEach((item, i) => {
      drawMissionaryCard(
        10 + (missionaryCardWidth + 4) * (i + 1),
        currentY,
        missionaryCardWidth,
        item.country,
        item.count,
        colors[(i + 1) % colors.length]
      );
    });
  }

  currentY += cardHeight + 10;

  // =====================
  // YTD SUMMARY TABLE
  // =====================
  checkNewPage(60);
  drawSectionHeader("Year-to-Date Summary");

  // Table header
  const rowH = compact ? 6 : 8;
  pdf.setFillColor(240, 240, 240);
  pdf.rect(10, currentY, pageWidth - 20, rowH, "F");
  pdf.setFontSize(compact ? 8 : 9);
  pdf.setFont("helvetica", "bold");
  pdf.setTextColor(60, 60, 60);
  pdf.text("Metric", 15, currentY + (compact ? 4.5 : 6));
  pdf.text("Current Period", 80, currentY + (compact ? 4.5 : 6));
  pdf.text("Prior Period", 120, currentY + (compact ? 4.5 : 6));
  pdf.text("YTD Total", 160, currentY + (compact ? 4.5 : 6));
  currentY += rowH + 2;

  // Table rows
  const tableData = [
    {
      label: "People Who Heard Gospel",
      current: ministryStats.currentMonth?.heardGospel || 0,
      prior: ministryStats.priorMonth?.heardGospel || 0,
      ytd: ministryStats.ytd?.heardGospel || 0,
    },
    {
      label: "People Who Received Jesus",
      current: ministryStats.currentMonth?.receivedJesus || 0,
      prior: ministryStats.priorMonth?.receivedJesus || 0,
      ytd: ministryStats.ytd?.receivedJesus || 0,
    },
    {
      label: "People Baptized",
      current: ministryStats.currentMonth?.baptized || 0,
      prior: ministryStats.priorMonth?.baptized || 0,
      ytd: ministryStats.ytd?.baptized || 0,
    },
    {
      label: "Bible Studies Started",
      current: bibleStudies,
      prior: priorBibleStudies,
      ytd: ytdBibleStudies,
    },
    {
      label: "Churches Planted",
      current: ministryStats.currentMonth?.churchesPlanted || 0,
      prior: ministryStats.priorMonth?.churchesPlanted || 0,
      ytd: ministryStats.ytd?.churchesPlanted || 0,
    },
    {
      label: "Leaders Trained",
      current: ministryStats.currentMonth?.leadersTrained || 0,
      prior: ministryStats.priorMonth?.leadersTrained || 0,
      ytd: ministryStats.ytd?.leadersTrained || 0,
    },
  ];

  const dataRowH = compact ? 5.5 : 7;
  pdf.setFontSize(compact ? 8 : 9);
  pdf.setFont("helvetica", "normal");
  tableData.forEach((row, index) => {
    if (index % 2 === 0) {
      pdf.setFillColor(250, 250, 250);
      pdf.rect(10, currentY, pageWidth - 20, dataRowH, "F");
    }
    pdf.setTextColor(60, 60, 60);
    pdf.text(row.label, 15, currentY + (compact ? 4 : 5));
    pdf.text(formatNumber(row.current), 80, currentY + (compact ? 4 : 5));
    pdf.text(formatNumber(row.prior), 120, currentY + (compact ? 4 : 5));
    pdf.setFont("helvetica", "bold");
    pdf.text(formatNumber(row.ytd), 160, currentY + (compact ? 4 : 5));
    pdf.setFont("helvetica", "normal");
    currentY += dataRowH;
  });

  // =====================
  // FOOTER
  // =====================
  const totalPages = pdf.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    pdf.setPage(i);
    pdf.setFontSize(8);
    pdf.setTextColor(128, 128, 128);

    // Page number
    pdf.text(`Page ${i} of ${totalPages}`, pageWidth / 2, pageHeight - (compact ? 6 : 10), {
      align: "center",
    });

    // Generation info
    const footerY = pageHeight - (compact ? 6 : 10);
    const generatedText = `Generated on ${new Date().toLocaleDateString()} at ${new Date().toLocaleTimeString()}`;
    pdf.text(generatedText, pageWidth - 15, footerY, { align: "right" });

    if (data.generatedBy) {
      pdf.text(`By: ${data.generatedBy}`, 15, footerY);
    }

    pdf.setTextColor(0, 0, 0);
  }

  // =====================
  // SAVE PDF
  // =====================
  // Build descriptive filename: e.g., 2026-02-all.pdf or 2026-02-India-Delhi.pdf
  const filterSuffix: string[] = [String(data.filters.year)];
  if (data.filters.month) {
    filterSuffix.push(String(data.filters.month).padStart(2, "0"));
  } else {
    filterSuffix.push("all");
  }
  if (data.filters.country) {
    filterSuffix.push(data.filters.country.replace(/\s+/g, "_"));
  }
  if (data.filters.state) {
    filterSuffix.push(data.filters.state.replace(/\s+/g, "_"));
  }
  if (data.filters.missionaryName) {
    // Extract missionary number if present, e.g. "Name (IN-DL-001)" -> "IN-DL-001"
    const numMatch = data.filters.missionaryName.match(/\(([^)]+)\)/);
    filterSuffix.push(numMatch ? numMatch[1] : data.filters.missionaryName.replace(/[^a-zA-Z0-9-]/g, "_"));
  }

  const fileName = `${filterSuffix.join("-")}.pdf`;

  try {
    const pdfBlob = pdf.output("blob");
    const pdfUrl = URL.createObjectURL(pdfBlob);
    const link = document.createElement("a");
    link.href = pdfUrl;
    link.download = fileName;
    link.style.display = "none";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setTimeout(() => URL.revokeObjectURL(pdfUrl), 1000);
  } catch (error) {
    console.error("Error generating/displaying PDF:", error);
    pdf.save(fileName);
  }
};

export default generateDashboardPDF;
